// UnifiedActivityTable.jsx — v2
// COLOR SCHEME matches UnifiedActivityCard:
//   BBC split incoming  → yellow-green  (#8C7A0A)
//   BBC split outgoing  → amber         (#CA8A04)
//   incoming invoice    → green         (#16A34A)
//   blitzpay            → red           (#DC2626)
//   transfer/withdrawal → blue          (#2563EB)
//   payout/outgoing     → blue / neutral

import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Button, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Paper, Chip, Avatar, Stack, CircularProgress,
  Alert, IconButton, Tooltip, ToggleButtonGroup, ToggleButton, Grid, Checkbox,
} from "@mui/material";
import {
  Edit as EditIcon, Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon, Payment as PaymentIcon,
  Receipt as ReceiptIcon, TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapHorizIcon, Person as PersonIcon,
  Group as GroupIcon, Business as BusinessIcon,
  Speed as SpeedIcon, Schedule as ScheduleIcon,
  HourglassEmpty as HourglassEmptyIcon, Warning as WarningIcon,
  ViewModule as ViewModuleIcon, ViewList as ViewListIcon,
  Info as InfoIcon, Assignment as AssignmentIcon,
  AccountBalance as AccountBalanceIcon, ArrowUpward as ArrowUpIcon,
  MergeType as MergeTypeIcon, Bolt as BoltIcon,
  Settings as SettingsIcon, CallSplit as CallSplitIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import UnifiedActivityCard from "./unifiedActivityCard";
import UnifiedDetailsDialog from "./unifiedDetailsDialog";

// ─────────────────────────────────────────────────────────────────────────────
// COLOR TOKENS — keep in sync with UnifiedActivityCard
// ─────────────────────────────────────────────────────────────────────────────

const YG        = "#8C7A0A";   // BBC split incoming — yellow-leaning chartreuse
const YG_LIGHT  = "#F0DC68";
const YG_DARK   = "#6E6008";
const YG_DEEP   = "#504505";
const YG_INK    = "#1A1400";
const YG_TINT   = alpha("#8C7A0A", 0.07);
const YG_BORDER = alpha("#8C7A0A", 0.35);

const Y        = "#CA8A04";    // BBC split outgoing — amber
const Y_LIGHT  = "#FDE68A";
const Y_DARK   = "#A16207";
const Y_DEEP   = "#78350F";
const Y_INK    = "#1C0A00";
const Y_TINT   = alpha("#CA8A04", 0.07);
const Y_BORDER = alpha("#CA8A04", 0.35);

const G        = "#16A34A";    // incoming invoice — green
const G_DARK   = "#15803D";
const G_DEEP   = "#14532D";
const G_INK    = "#FFFFFF";
const G_TINT   = alpha("#16A34A", 0.07);
const G_BORDER = alpha("#16A34A", 0.30);

const R        = "#DC2626";    // BlitzPay — red
const R_DARK   = "#B91C1C";
const R_DEEP   = "#7F1D1D";
const R_INK    = "#FFFFFF";
const R_TINT   = alpha("#DC2626", 0.07);
const R_BORDER = alpha("#DC2626", 0.28);

const B        = "#2563EB";    // transfer / withdrawal / payout — blue
const B_DARK   = "#1D4ED8";
const B_DEEP   = "#1E3A8A";
const B_INK    = "#FFFFFF";
const B_TINT   = alpha("#2563EB", 0.07);
const B_BORDER = alpha("#2563EB", 0.28);

const currency = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" });

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const isBBCSplit = (item) =>
  item?.invoice_type === "bbc" || item?.source_type === "bbc" || item?.isBBC;

const isMergeEligible = (item) => {
  const status = (item.status || item.invoice_status || "").toLowerCase();
  const isBlitzPayItem =
    item.isBlitzPay === true ||
    item.recordType === "blitzpay-invoice" ||
    item.invoice_type === "blitzpay" ||
    item.source_type === "blitzpay" ||
    item.blitzpay === true;
  return isBlitzPayItem && status === "funded";
};

const categorizeItem = (item) => {
  if (item.isIncoming && isBBCSplit(item))  return "bbc-split-incoming";
  if (!item.isIncoming && isBBCSplit(item)) return "bbc-split-outgoing";
  if (item.recordType === "transfer" || item.type === "Internal Transfer" || item.transaction_type === "internal_transfer") return "transfer";
  if (item.recordType === "withdrawal" || item.type === "Withdrawal") return "withdrawal";
  if (item.type === "Payout" || item.recordType === "payout") return "payout";
  if (item.isIncoming || item.invoice_type === "creator") return "invoice-incoming";
  if (item.isBlitzPay || item.recordType === "blitzpay-invoice" || item.invoice_type === "blitzpay" || item.source_type === "blitzpay") return "blitzpay";
  return "invoice-outgoing";
};

// Per-type visual config used in both table and card grid
const TYPE_CONFIG = {
  "bbc-split-incoming": {
    label: "Split Received", pillBg: YG_LIGHT, pillInk: YG_INK,
    avatarBg: YG, avatarInk: YG_INK,
    rowBg: YG_TINT, rowBorder: YG, rowHover: alpha(YG, 0.12),
    amountColor: YG_DEEP,
    icon: <CallSplitIcon sx={{ fontSize: 18 }} />,
  },
  "bbc-split-outgoing": {
    label: "Split Sent", pillBg: Y_LIGHT, pillInk: Y_INK,
    avatarBg: Y, avatarInk: Y_INK,
    rowBg: Y_TINT, rowBorder: Y, rowHover: alpha(Y, 0.12),
    amountColor: Y_DEEP,
    icon: <BoltIcon sx={{ fontSize: 18 }} />,
  },
  "transfer": {
    label: "Transfer", pillBg: B, pillInk: B_INK,
    avatarBg: B, avatarInk: B_INK,
    rowBg: B_TINT, rowBorder: B, rowHover: alpha(B, 0.1),
    amountColor: B_DEEP,
    icon: <SwapHorizIcon sx={{ fontSize: 18 }} />,
  },
  "withdrawal": {
    label: "Withdrawal", pillBg: B, pillInk: B_INK,
    avatarBg: B, avatarInk: B_INK,
    rowBg: B_TINT, rowBorder: B, rowHover: alpha(B, 0.1),
    amountColor: R_DEEP,  // withdrawals are always outgoing — red amount
    icon: <ArrowUpIcon sx={{ fontSize: 18 }} />,
  },
  "payout": {
    label: "Payout", pillBg: B, pillInk: B_INK,
    avatarBg: B, avatarInk: B_INK,
    rowBg: B_TINT, rowBorder: B, rowHover: alpha(B, 0.1),
    amountColor: B_DEEP,
    icon: <PaymentIcon sx={{ fontSize: 18 }} />,
  },
  "blitzpay": {
    label: "BlitzPay", pillBg: R, pillInk: R_INK,
    avatarBg: R, avatarInk: R_INK,
    rowBg: R_TINT, rowBorder: R, rowHover: alpha(R, 0.1),
    amountColor: R_DEEP,
    icon: <SwapHorizIcon sx={{ fontSize: 18 }} />,
  },
  "invoice-incoming": {
    label: "Incoming", pillBg: G, pillInk: G_INK,
    avatarBg: G, avatarInk: G_INK,
    rowBg: G_TINT, rowBorder: G, rowHover: alpha(G, 0.1),
    amountColor: G_DEEP,
    icon: <TrendingUpIcon sx={{ fontSize: 18 }} />,
  },
  "invoice-outgoing": {
    label: "Outgoing", pillBg: null, pillInk: null,
    avatarBg: null, avatarInk: null,
    rowBg: null, rowBorder: null, rowHover: null,
    amountColor: "primary.main",
    icon: <ReceiptIcon sx={{ fontSize: 18 }} />,
  },
};
const getTypeConfig = (itemType) => TYPE_CONFIG[itemType] || {
  label: "Unknown", pillBg: null, pillInk: null,
  avatarBg: "grey.400", avatarInk: "#fff",
  rowBg: null, rowBorder: null, rowHover: null,
  amountColor: "text.primary",
  icon: <ReceiptIcon sx={{ fontSize: 18 }} />,
};

const getEntityInfo = (item, itemType) => {
  if (itemType === "bbc-split-incoming") {
    const senderName = item.client_name || item.company_name || item.name || "Agency";
    const recipientCount = item.creator_payments?.length || item.recipient_count || item.custom_details?.recipient_count || null;
    const preCount = item.pre_accepted_count || (item.creator_payments || []).filter((p) => p.preapproved || p.blitzpay_preapproved).length || 0;
    return { name: senderName, subtitle: recipientCount ? `${recipientCount} contractor${recipientCount !== 1 ? "s" : ""}${preCount > 0 ? ` · ${preCount} pre-accepted` : ""}` : "Blitz Split invoice" };
  }
  if (itemType === "bbc-split-outgoing") {
    const clientName = item.client_name || item.name || "Enterprise Client";
    const recipientCount = item.recipient_count || item.custom_details?.recipient_count || null;
    const s = (item.status || "").toLowerCase();
    const statusLabel = s === "approved" ? "Approved — awaiting funding" : (s === "paid" || s === "funded") ? "Funded" : "Awaiting client approval";
    return { name: clientName, subtitle: recipientCount ? `${recipientCount} contractor${recipientCount !== 1 ? "s" : ""} · ${statusLabel}` : statusLabel };
  }
  if (itemType === "transfer")   return { name: item.name || item.brief || "Balance Transfer", subtitle: item.description || "Internal transfer" };
  if (itemType === "withdrawal") return { name: item.name || `Withdrawal to ${item.payment_service || "Bank"}`, subtitle: item.description || item.notes || "Bank transfer" };
  if (itemType === "payout") {
    const ci = item.creator_info || {};
    const id = item.creator_id || ci.creator_id;
    return { name: ci.display_name || item.creator_display_name || item.name || id || "Creator", subtitle: id ? `@${id}` : null };
  }
  if (itemType === "blitzpay") {
    const isMulti = item.isMultiRecipient && item.recipientCount > 1;
    if (isMulti) {
      const payouts = item.payoutDetailsArray || [];
      const names = payouts.map((pd) => pd.name || pd.creator_display_name || pd.creator_id || null).filter(Boolean);
      const MAX = 2;
      const displayName = names.length === 0 ? (item.campaign_name || "BlitzPay to Multiple Creators") : names.length <= MAX ? `BlitzPay to ${names.join(", ")}` : `BlitzPay to ${names.slice(0, MAX).join(", ")} +${names.length - MAX} more`;
      return { name: displayName, subtitle: `${item.recipientCount} creators` };
    }
    const displayName = item.campaign_name || item.name || "BlitzPay Payment";
    const creatorId = item.creator_id;
    const isBatch = !creatorId || displayName?.toLowerCase().includes("batch") || displayName?.toLowerCase().includes("multiple");
    return { name: displayName, subtitle: isBatch ? "Multiple creators" : (creatorId ? `@${creatorId}` : "Creator payment") };
  }
  if (itemType === "invoice-incoming") {
    const senderName = item.client_name || item.name || item.creator_id;
    return { name: senderName || "Vendor", subtitle: item.creator_id ? `@${item.creator_id}` : null };
  }
  const recipientName = item.client_name || item.name || item.creator_id;
  return { name: recipientName || "Client", subtitle: item.creator_id ? `@${item.creator_id}` : null };
};

const deriveFeePayer = (item) => {
  const firstPayout = item.payoutDetailsArray?.[0];
  const explicit = item.fee_payer || item.custom_details?.fee_payer || item.feeBreakdown?.feePayer || item.payoutDetails?.fee_payer || firstPayout?.fee_payer;
  if (explicit) return explicit;
  const role = firstPayout?.role || item.payoutDetails?.role || item.role;
  if (role === "Client")  return "me";
  if (role === "Creator") return "creator";
  const rate = firstPayout?.interest_rate ?? item.payoutDetails?.interest_rate ?? item.interest_rate;
  if (rate != null) return Number(rate) === 0 ? "me" : "creator";
  return null;
};

const needsApproval = (item, itemType) => {
  if (["transfer", "withdrawal", "bbc-split-outgoing"].includes(itemType)) return false;
  const status = (item.status || item.invoice_status || "").toLowerCase();
  if (itemType === "bbc-split-incoming") return status === "pending";
  return ["invoice-incoming", "invoice-outgoing", "blitzpay"].includes(itemType) && status === "pending";
};
const needsManage  = (itemType) => itemType === "bbc-split-outgoing";
const isEditable   = (item, itemType) => {
  if (["transfer", "withdrawal", "blitzpay", "payout", "invoice-incoming", "bbc-split-incoming", "bbc-split-outgoing"].includes(itemType)) return false;
  return !["paid", "completed", "funded"].includes((item.status || "").toLowerCase());
};
const isViewable   = (item) => ["paid", "completed", "funded", "approved"].includes((item.status || item.invoice_status || "").toLowerCase());

// ─────────────────────────────────────────────────────────────────────────────
// PILL — same style as UnifiedActivityCard's TypePill
// ─────────────────────────────────────────────────────────────────────────────

const TypePill = ({ bg, ink, icon, label, theme }) => (
  <Box sx={{
    display: "inline-flex", alignItems: "center", gap: 0.4,
    px: 0.875, height: 22, borderRadius: "5px",
    bgcolor: bg || (theme?.palette.grey[200]),
    maxWidth: "100%", overflow: "hidden",
  }}>
    {React.cloneElement(icon, { sx: { fontSize: 10, flexShrink: 0, color: ink || "text.primary" } })}
    <Typography sx={{ fontWeight: 800, color: ink || "text.primary", fontSize: "0.67rem", lineHeight: 1, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// EXTERNAL PO CELL
// ─────────────────────────────────────────────────────────────────────────────

const ExternalPOCell = ({ externalPoNumber, erpSystemName }) => (
  externalPoNumber ? (
    <Tooltip title={erpSystemName ? `${erpSystemName} PO` : "External PO"} arrow>
      <Chip
        icon={<AssignmentIcon sx={{ fontSize: 11 }} />}
        label={externalPoNumber}
        size="small" color="info" variant="outlined"
        sx={{ height: 18, fontSize: "0.67rem", fontWeight: 600, mt: 0.5 }}
      />
    </Tooltip>
  ) : (
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.67rem", mt: 0.5, display: "block" }}>
      Click to add PO
    </Typography>
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// STATUS CHIP — same height 22px
// ─────────────────────────────────────────────────────────────────────────────

const getStatusChip = (status) => {
  const s = (status || "").toLowerCase();
  const cfg = {
    pending:    { color: "warning", icon: <HourglassEmptyIcon /> },
    approved:   { color: "info",    icon: <CheckCircleIcon /> },
    completed:  { color: "success", icon: <CheckCircleIcon /> },
    paid:       { color: "success", icon: <CheckCircleIcon /> },
    funded:     { color: "success", icon: <CheckCircleIcon /> },
    cancelled:  { color: "error",   icon: null },
    declined:   { color: "error",   icon: null },
    processing: { color: "warning", icon: <HourglassEmptyIcon /> },
  };
  const c = cfg[s] || { color: "default", icon: null };
  return <Chip label={status} size="small" color={c.color} icon={c.icon || undefined} sx={{ fontWeight: 600, textTransform: "capitalize", fontSize: "0.72rem", height: 22 }} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// BBC STATUS PILL (table row right-side)
// ─────────────────────────────────────────────────────────────────────────────

const getBBCStatusProps = (status, isOutgoing) => {
  const s = (status || "").toLowerCase();
  if (isOutgoing) {
    if (s === "pending")                return { label: "Awaiting Approval",   bg: Y_TINT,  fg: Y_DEEP,  border: Y_BORDER  };
    if (s === "approved")               return { label: "Approved · Electing", bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "paid" || s === "funded") return { label: "Funded & Settled",    bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "cancelled")              return { label: "Cancelled",            bg: R_TINT,  fg: R_DEEP,  border: R_BORDER  };
                                        return { label: status,                 bg: Y_TINT,  fg: Y_DEEP,  border: Y_BORDER  };
  } else {
    if (s === "pending")                return { label: "Action Required",     bg: YG_TINT, fg: YG_DEEP, border: YG_BORDER };
    if (s === "approved")               return { label: "Approved · Settling", bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "paid" || s === "funded") return { label: "Funded",              bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "cancelled")              return { label: "Cancelled",            bg: R_TINT,  fg: R_DEEP,  border: R_BORDER  };
                                        return { label: status,                 bg: YG_TINT, fg: YG_DEEP, border: YG_BORDER };
  }
};

const BBCStatusPill = ({ status, isOutgoing }) => {
  const p = getBBCStatusProps(status, isOutgoing);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", px: 1, height: 22, borderRadius: "5px", bgcolor: p.bg, border: `1px solid ${p.border}`, maxWidth: 160 }}>
      <Typography noWrap sx={{ fontWeight: 700, color: p.fg, fontSize: "0.67rem", lineHeight: 1 }}>{p.label}</Typography>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CARD GRID VIEW
// ─────────────────────────────────────────────────────────────────────────────

const CardGridView = ({ items, onEdit, onApprove, onView, onViewDetails, selectedIds, onToggleSelect }) => {
  const hasAnyMergeEligible = items.some(isMergeEligible);
  return (
    <Grid container spacing={3}>
      {items.map((item, index) => {
        const canMerge = isMergeEligible(item);
        return (
          <Grid item xs={12} sm={6} md={4} key={`card-${item.id}-${index}`}>
            <Box sx={{ position: "relative", height: "100%" }}>
              {hasAnyMergeEligible && canMerge && (
                <Checkbox
                  checked={selectedIds?.has(item.id) || false}
                  onChange={() => onToggleSelect?.(item.id)}
                  size="small"
                  sx={{ position: "absolute", top: 4, left: 4, zIndex: 1 }}
                />
              )}
              <UnifiedActivityCard
                item={item}
                onEdit={onEdit}
                onApprove={onApprove}
                onView={onView}
                onViewDetails={onViewDetails}
              />
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP TABLE VIEW
// ─────────────────────────────────────────────────────────────────────────────

const DesktopTable = ({ items, onEdit, onApprove, onViewDetails, theme }) => (
  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: alpha(theme.palette.background.default, 0.6) }}>
          <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", width: "16%", py: 1.5 }}>Type</TableCell>
          <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", width: "22%", py: 1.5 }}>Entity</TableCell>
          <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", width: "14%", py: 1.5 }}>PO / ID</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem", width: "13%", py: 1.5 }}>Amount</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", width: "14%", py: 1.5 }}>Status</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", width: "10%", py: 1.5 }}>Date</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, fontSize: "0.75rem", width: "11%", py: 1.5 }}>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, index) => {
          const itemType   = categorizeItem(item);
          const tc         = getTypeConfig(itemType);
          const entityInfo = getEntityInfo(item, itemType);
          const reviewNeeded = needsApproval(item, itemType);
          const manageSplit  = needsManage(itemType);
          const canEdit      = isEditable(item, itemType);
          const completed    = isViewable(item);

          const isIncomingBBC = itemType === "bbc-split-incoming";
          const isOutgoingBBC = itemType === "bbc-split-outgoing";
          const isAnyBBC      = isIncomingBBC || isOutgoingBBC;
          const isTransfer    = itemType === "transfer";
          const isWithdrawal  = itemType === "withdrawal";
          const isBlitzPayType = itemType === "blitzpay";

          const status        = (item.status || item.invoice_status || "pending").toLowerCase();
          const poNumber      = item.po_number || item.unique_code || `#${item.id}`;
          const date          = item.payout_date || item.created_at || item.date;
          const formattedDate = date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
          const amount        = Number(item.amount || item.amount_due || item.net_amount || 0);
          const isCredit      = item.direction === "credit";
          const showExternalPO = isBlitzPayType && !reviewNeeded;
          const isBlitzPayTiming = item.blitzpay || item.payment_timing === "instant";
          const isMulti       = item.isMultiRecipient && item.recipientCount > 1;
          const fp            = deriveFeePayer(item);
          const youPaidFee    = fp === "me";

          // Row tint — action-required rows get a stronger left border
          const rowBg     = reviewNeeded || manageSplit ? tc.rowBg : alpha(tc.rowBg || theme.palette.background.paper, 0.4);
          const leftBorder = (reviewNeeded || manageSplit) ? `4px solid ${tc.rowBorder}` : `4px solid transparent`;

          return (
            <TableRow
              key={`${itemType}-${item.id || index}`}
              sx={{
                bgcolor: rowBg || "background.paper",
                borderLeft: leftBorder,
                cursor: showExternalPO ? "pointer" : "default",
                transition: "background-color 0.15s ease",
                "&:hover": {
                  bgcolor: tc.rowHover || alpha(theme.palette.action.hover, 0.04),
                },
                "& td": { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` },
              }}
              onClick={showExternalPO ? () => onViewDetails?.(item) : undefined}
            >
              {/* ── Type ────────────────────────────────────────────────── */}
              <TableCell sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar sx={{ width: 34, height: 34, flexShrink: 0, bgcolor: tc.avatarBg || `${(itemType === "invoice-outgoing" ? "secondary" : "primary")}.main`, color: tc.avatarInk || "#fff" }}>
                    {React.cloneElement(tc.icon, { sx: { fontSize: 17 } })}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <TypePill bg={tc.pillBg} ink={tc.pillInk} icon={tc.icon} label={tc.label} theme={theme} />
                    {/* Sub-label for credit/debit on transfers */}
                    {isTransfer && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip label={isCredit ? "Credit" : "Debit"} size="small" color={isCredit ? "success" : "error"} variant="outlined" sx={{ height: 18, fontSize: "0.63rem" }} />
                      </Box>
                    )}
                    {/* BBC payment term */}
                    {isAnyBBC && item.payment_term && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={`Net ${item.payment_term}`} size="small" variant="outlined"
                          sx={{ height: 18, fontSize: "0.63rem", fontWeight: 700, borderColor: isIncomingBBC ? YG_BORDER : Y_BORDER, color: isIncomingBBC ? YG_DEEP : Y_DEEP }}
                        />
                      </Box>
                    )}
                    {/* Payout timing */}
                    {itemType === "payout" && (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip icon={isBlitzPayTiming ? <SpeedIcon /> : <ScheduleIcon />} label={isBlitzPayTiming ? "Instant" : "Net 30"} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.63rem" }} />
                      </Box>
                    )}
                  </Box>
                </Stack>
              </TableCell>

              {/* ── Entity ──────────────────────────────────────────────── */}
              <TableCell sx={{ py: 1.5, maxWidth: 200 }}>
                <Typography variant="body2" fontWeight={600} noWrap title={entityInfo.name} sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {entityInfo.name}
                </Typography>
                {entityInfo.subtitle && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ overflow: "hidden", textOverflow: "ellipsis", fontSize: "0.7rem" }}>
                    {entityInfo.subtitle}
                  </Typography>
                )}
              </TableCell>

              {/* ── PO / ID ─────────────────────────────────────────────── */}
              <TableCell sx={{ py: 1.5 }}>
                <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: "0.78rem", wordBreak: "break-all" }}>
                  {(poNumber || "").slice(0, 20)}{(poNumber || "").length > 20 ? "…" : ""}
                </Typography>
                {showExternalPO && (
                  <ExternalPOCell
                    externalPoNumber={item.external_po_number}
                    erpSystemName={item.erp_system || item.vendor_system_name}
                  />
                )}
              </TableCell>

              {/* ── Amount ──────────────────────────────────────────────── */}
              <TableCell align="right" sx={{ py: 1.5 }}>
                {item.feeBreakdown?.isFunded ? (() => {
                  const creatorsReceive = item.totalPayoutNetAmount || (item.feeBreakdown.invoiceAmount - item.feeBreakdown.platformFee);
                  return (
                    <Tooltip
                      title={`Vendors receive: ${currency.format(creatorsReceive)} · Platform fee: ${currency.format(item.feeBreakdown.platformFee)} · Paid by: ${fp === "me" ? "You" : fp === "creator" ? "Vendors" : "Unknown"}`}
                      arrow
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={700} color="warning.main" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {currency.format(item.feeBreakdown.invoiceAmount)}
                        </Typography>
                        {fp ? (
                          <Chip size="small" label={youPaidFee ? "You paid fee" : "Vendors pay fee"} color={youPaidFee ? "success" : "warning"} variant="outlined" sx={{ height: 16, fontSize: "0.58rem", fontWeight: 700, mt: 0.25 }} />
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                            (incl. {item.feeBreakdown.feePercentage}% fee)
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })() : (
                  <Typography variant="body2" fontWeight={700} sx={{ color: (isTransfer || isWithdrawal) ? (isCredit ? G_DEEP : R_DEEP) : tc.amountColor, fontVariantNumeric: "tabular-nums" }}>
                    {(isTransfer || isWithdrawal) && (isCredit ? "+" : "-")}
                    {currency.format(amount)}
                  </Typography>
                )}
              </TableCell>

              {/* ── Status ──────────────────────────────────────────────── */}
              <TableCell align="center" sx={{ py: 1.5 }}>
                {reviewNeeded ? (
                  <Chip
                    label="Action Required" size="small" icon={<WarningIcon sx={{ fontSize: "13px !important", color: `${isIncomingBBC ? YG_DEEP : "warning.dark"} !important` }} />}
                    sx={isIncomingBBC ? { fontWeight: 700, bgcolor: YG_TINT, color: YG_DEEP, border: `1px solid ${YG_BORDER}`, fontSize: "0.68rem", height: 22 } : { fontWeight: 600, height: 22 }}
                    color={isIncomingBBC ? undefined : "warning"}
                  />
                ) : isAnyBBC ? (
                  <BBCStatusPill status={status} isOutgoing={isOutgoingBBC} />
                ) : (
                  getStatusChip(status)
                )}
              </TableCell>

              {/* ── Date ────────────────────────────────────────────────── */}
              <TableCell align="center" sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                  {formattedDate}
                </Typography>
              </TableCell>

              {/* ── Actions ─────────────────────────────────────────────── */}
              <TableCell align="center" sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  {reviewNeeded && isIncomingBBC ? (
                    <Button variant="contained" size="small" onClick={() => onApprove?.(item.id)} startIcon={<CallSplitIcon />}
                      sx={{ borderRadius: 1.5, fontWeight: 700, minWidth: 110, textTransform: "none", bgcolor: YG, color: YG_INK, "&:hover": { bgcolor: YG_DARK } }}>
                      Review Split
                    </Button>
                  ) : reviewNeeded && itemType === "invoice-incoming" ? (
                    <Button variant="contained" size="small" onClick={() => onApprove?.(item.id)} startIcon={<CheckCircleIcon />}
                      sx={{ borderRadius: 1.5, fontWeight: 700, minWidth: 110, textTransform: "none", bgcolor: G, color: G_INK, "&:hover": { bgcolor: G_DARK } }}>
                      Approve
                    </Button>
                  ) : reviewNeeded ? (
                    <Button variant="contained" color="warning" size="small" onClick={() => onApprove?.(item.id)} startIcon={<CheckCircleIcon />}
                      sx={{ borderRadius: 1.5, fontWeight: 600, minWidth: 90, textTransform: "none" }}>
                      Review
                    </Button>
                  ) : manageSplit ? (
                    <Button variant="contained" size="small" onClick={() => onApprove?.(item.id)} startIcon={<SettingsIcon />}
                      sx={{ borderRadius: 1.5, fontWeight: 700, minWidth: 110, textTransform: "none", bgcolor: Y, color: Y_INK, "&:hover": { bgcolor: Y_DARK } }}>
                      Manage Split
                    </Button>
                  ) : completed || isTransfer || isWithdrawal ? (
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => onViewDetails?.(item)}
                        sx={{ color: tc.amountColor || "text.secondary" }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => onViewDetails?.(item)} sx={{ color: tc.amountColor || "primary.main" }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => onEdit?.(item)} color="secondary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const UnifiedActivityTable = ({
  items = [],
  onEdit,
  onApprove,
  onView,
  onMerge,
  isMerging = false,
  isLoading = false,
  emptyMessage = "No activity found",
  isMobile = false,
  defaultView = "cards",
}) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState(isMobile ? "cards" : defaultView);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedMergeIds, setSelectedMergeIds] = useState(new Set());

  const visibleIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  useEffect(() => {
    setSelectedMergeIds((prev) => {
      const pruned = new Set([...prev].filter((id) => visibleIds.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleIds]);

  const handleToggleMergeSelect = (id) => {
    setSelectedMergeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleMerge = () => {
    if (selectedMergeIds.size >= 2 && onMerge) {
      onMerge([...selectedMergeIds]);
      setSelectedMergeIds(new Set());
    }
  };

  const handleViewDetails = (item) => { setSelectedItem(item); setDetailsDialogOpen(true); };
  const handleCloseDetails = () => { setDetailsDialogOpen(false); setSelectedItem(null); };

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
      <CircularProgress />
      <Typography variant="body1" sx={{ ml: 2 }}>Loading activity…</Typography>
    </Box>
  );

  if (!items?.length) return (
    <Alert severity="info" sx={{ borderRadius: 2 }}>
      <Typography variant="body1">{emptyMessage}</Typography>
    </Alert>
  );

  return (
    <Box>
      {/* ── View toggle (desktop only) ─────────────────────────────── */}
      {!isMobile && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {items.length} {items.length === 1 ? "item" : "items"}
          </Typography>
          <ToggleButtonGroup
            value={viewMode} exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", "& .MuiToggleButton-root": { px: 1.5, py: 0.5, fontSize: "0.8rem" } }}
          >
            <ToggleButton value="cards" aria-label="card view">
              <ViewModuleIcon sx={{ mr: 0.5, fontSize: 15 }} />Cards
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <ViewListIcon sx={{ mr: 0.5, fontSize: 15 }} />Table
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* ── Merge action bar ───────────────────────────────────────── */}
      {selectedMergeIds.size > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {selectedMergeIds.size} funded BlitzPay{selectedMergeIds.size !== 1 ? "s" : ""} selected
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setSelectedMergeIds(new Set())}>Clear</Button>
          <Button
            size="small" variant="contained" startIcon={<MergeTypeIcon />}
            disabled={selectedMergeIds.size < 2 || isMerging}
            onClick={handleMerge}
          >
            {isMerging ? "Merging…" : "Merge Selected"}
          </Button>
        </Box>
      )}

      {/* ── Main content ───────────────────────────────────────────── */}
      {viewMode === "cards" ? (
        <CardGridView
          items={items}
          onEdit={onEdit}
          onApprove={onApprove}
          onView={onView}
          onViewDetails={handleViewDetails}
          selectedIds={selectedMergeIds}
          onToggleSelect={handleToggleMergeSelect}
        />
      ) : (
        <DesktopTable
          items={items}
          onEdit={onEdit}
          onApprove={onApprove}
          onView={onView}
          onViewDetails={handleViewDetails}
          theme={theme}
        />
      )}

      <UnifiedDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        item={selectedItem}
      />
    </Box>
  );
};

export default UnifiedActivityTable;